import {useLazyQuery} from "@apollo/client";
import classNames from "classnames";
import {ForwardedRef, forwardRef, useEffect, useState} from "react";
import {useSearchParams} from "react-router";
import {SelectInstance} from "react-select";

import {Select} from "@/components/select/Select.tsx";
import {SelectProps} from "@/components/select/Select.types.ts";
import {GQL_RUM_URLS, RUMUrl, RUMUrlsResponse} from "@/lib/queries/rum.ts";

import styles from "./RUMURLSearch.module.scss";

export interface RUMURLSearch extends Pick<RUMUrl, "URLGroup" | "URLGroupHash"> {}

type RUMURLSearchProps = SelectProps<RUMURLSearch> & {
  urlId: number;
  urlHash?: string;
  start?: Date;
  end?: Date;
};

export const RUMURLGroupHashSearch = forwardRef(
  (
    {start, end, urlId, urlHash, onChange, ...props}: RUMURLSearchProps,
    ref: ForwardedRef<SelectInstance<RUMURLSearch>>
  ) => {
    const [value, setValue] = useState<RUMURLSearch | null>(null);
    const [searchParams] = useSearchParams();
    const [searchURLs, {loading}] = useLazyQuery<RUMUrlsResponse>(GQL_RUM_URLS);

    const url = searchParams.get("url");
    useEffect(() => {
      if (url && !loading && url != value?.URLGroupHash) {
        searchURLs({
          variables: {
            q: "",
            start: start,
            end: end,
            id: urlId,
            urlHash: url,
          },
        }).then(({data}) => {
          const newValue = data?.rumURLs.data.find((x) => x.URLGroupHash === url);
          if (newValue) {
            setValue(newValue);
            if (onChange) {
              onChange(newValue, {action: "select-option", option: newValue});
            }
          }
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, loading, searchURLs, setValue, start, end, urlId]);
    return (
      <Select
        ref={ref}
        async={true}
        className={classNames("me-2", styles.rumUrlSearch)}
        placeholder={`Search URL`}
        value={value}
        cacheOptions
        defaultOptions
        loadOptions={(inputValue, setOptions) => {
          searchURLs({
            variables: {
              q: inputValue,
              start: start,
              end: end,
              urlHash: urlHash,
              id: urlId,
            },
          }).then(({data}) => {
            setOptions(data?.rumURLs?.data || []);
          });
        }}
        getOptionValue={(o) => o.URLGroupHash.toString()}
        getOptionLabel={(o) => o.URLGroup}
        onChange={(v, actionMeta) => {
          setValue(v);
          if (onChange) {
            onChange(v, actionMeta);
          }
        }}
        isLoading={loading}
        isClearable
        {...props}
      />
    );
  }
);
